import Error404 from 'pages/Error/Error404';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchCampaign } from 'slices/campaigns/thunk';
import { AppDispatch, RootState } from 'store';
import { Nav, NavLink, NavItem } from 'reactstrap';
import InfluencerPostModal from 'Components/influencer/InfluencerPostModal';
import CampaignPaymentsOffcanvas from 'Components/campaigns/CampaignPaymentsOffcanvas';
import { setHeaderCampaignData, setLastActiveCampaignTab, toggleApplicationOffcanvas, toggleApproveApplicationCostModal, togglePostPerformanceOffcanvas } from 'slices/ui/reducer';
import ApplicationOffCanvas from 'Components/campaigns/applications/ApplicationOffCanvas';
import PostPerformanceOffcanvas from 'Components/campaigns/collaborations/PostPerformanceOffcanvas';
import AccessControl from 'wrappers/AccessControl';
import { PRIVILEGE } from 'constants/privilege';
import { encodeBase64 } from 'utils/helpers';
import { defaultApplicationFilters } from 'Components/campaigns/applications/ApplicationFilters';
import { defaultCollaborationFilters } from 'pages/Campaigns/Collaborations';
import ApproveApplicationCost from 'Components/campaigns/applications/ApproveApplicationCost';
import Loader from 'Components/Common/Loader';

interface CampaignLayoutProps {
    children: any,
    filters?: React.ReactNode
}

const CampaignLayout: React.FC<CampaignLayoutProps> = ({ filters, children }) => {
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();
    const { campaignId } = useParams();

    const loading = useSelector((state: RootState) => state.campaigns.loading);

    const campaignDetail = useSelector((state: RootState) => state.campaigns.items[campaignId!]);
    const campaignData = campaignDetail?.data ?? null;
    const configurations = useSelector((state: RootState) => state.auth.configurations);
    

    const totalApplicationsCount = useSelector((state: RootState) => state.campaignApplications.campaigns[campaignId!]?.total) ?? 0;
    const filteredApplicationsCount = useSelector((state: RootState) => state.campaignApplications.campaigns[campaignId!]?.filtered_lists?.[state.campaignApplications.applied_filter_id!].count) ?? 0;

    const totalCollaborationsCount = useSelector((state: RootState) => state.collaborations.campaigns[campaignId!]?.filtered_lists?.[state.collaborations.applied_filter_id!]?.count) ?? 0;
    const totalPostsCount = useSelector((state: RootState) => state.campaignPermalinks.campaigns[campaignId!]?.filtered_lists?.[state.campaignPermalinks.applied_filter_id!]?.count) ?? 0;

    const influencerPostModalVisibile = useSelector((state: RootState) => state.ui.influencerPostModal);

    const applicationOffcanvas = useSelector((state: RootState) => state.ui.applicationOffcanvas);
    const postPerformanceOffcanvas = useSelector((state: RootState) => state.ui.postPerformanceOffcanvas);
    const approveApplicationModal = useSelector((state: RootState) => state.ui.approveApplicationCostModal);

    const applicationFilters = useSelector((state: RootState) => state.campaignApplications.filters);
    const collaborationFilters = useSelector((state: RootState) => state.collaborations.filters);

    useEffect(() => {
        if (campaignId && !campaignData) {
            dispatch(fetchCampaign({ campaignId }));
        }
    }, [campaignId, dispatch, campaignData]);

    useEffect(() => {
        if (campaignData) {
            dispatch(setHeaderCampaignData({ campaign: campaignData }));
        }

        return () => {
            if (campaignData) {
                dispatch(setHeaderCampaignData({ campaign: null, detailPage: false }));
            }
        }
    }, [campaignData])

    const getTabId = useMemo(() => {
        const path = window.location.pathname;
        if (path.includes('/applications')) return 'applications';
        if (path.includes('/collaborations')) return 'collaborations';
        if (path.includes('/insights')) return 'insights';
        if (path.includes('/add-posts')) return 'add-posts';
        if (path.includes('/links-status')) return 'links-status';
        return '';
    }, [window.location.pathname]);

    const getPageTitle = useMemo(() => {
        if (!campaignData) return '';

        switch (getTabId) {
            case 'applications':
                return 'Applications Received';
            case 'collaborations':
                return 'Collaborations';
            case 'insights':
                return 'Campaign Insights';
            default:
                return 'Campaign';
        }
    }, [campaignData, getTabId]);

    useEffect(() => {
        dispatch(setLastActiveCampaignTab(getTabId as 'applications' | 'collaborations' | 'insights'))
    }, [getTabId])

    useEffect(() => {
        if (campaignData && campaignData?.campaign?.mode === 'offline' && getTabId === 'applications') {
            navigate(`/campaign/${campaignId}/insights`)
        } else if(campaignData && campaignData?.campaign?.mode !== 'offline' && getTabId === 'add-posts'){
            navigate(`/campaign/${campaignId}/applications`)
        }
    }, [campaignData, getTabId])

    const getRecentFiltersQuery = (dataSet: 'applications' | 'collaborations') => {
        let filters: any = null;

        switch (dataSet) {
            case 'applications':
                filters = applicationFilters ?? defaultApplicationFilters;
                break;
            case 'collaborations':
                filters = collaborationFilters ?? defaultCollaborationFilters;
                break;
            default:
                filters = null;
        }

        if (!filters) return '';
        return `?filters=${encodeBase64({ ...filters, page: 1 })}`;

    }

    if (loading && !campaignData) return <Loader />;

    if ((!loading && !campaignData) || !campaignId) return <Error404 />;

    return (
        <div>
            <div className="campaign-page">
                <h1 className="campaign-page-title">{getPageTitle}</h1>
                <div className="d-flex justify-content-between align-items-center campaign-page-info flex-column flex-lg-row mt-4 gap-2 gap-lg-0">
                    <Nav tabs className="nav nav-tabs nav-tabs-custom nav-primary nav-justified campaign-nav w-100 w-lg-50">
                        <AccessControl privilege={PRIVILEGE.CAMPAIGN.APPLICATION.INDEX} ifCondition={campaignData?.campaign?.mode !== 'offline'}>
                            <NavItem className='campaign-nav-item'>
                                <NavLink
                                    style={{ cursor: "pointer" }}
                                    className={`campaign-nav-link ${getTabId === 'applications' && 'active-campaign-nav'} text-start`}
                                    onClick={() => navigate(`/campaign/${campaignId}/applications${getRecentFiltersQuery('applications')}`)}
                                >
                                    Applications Received
                                    {
                                        totalApplicationsCount ? (
                                            <React.Fragment>
                                                <span className="count-badge mx-2">{filteredApplicationsCount} <span className='text-muted'>of</span> {totalApplicationsCount}</span>
                                            </React.Fragment>
                                        ) : (
                                            <span className="count-badge mx-2">{campaignData?.campaign?.applications_count ?? 0}</span>
                                        )
                                    }
                                </NavLink>
                            </NavItem>
                        </AccessControl>
                        <AccessControl privilege={PRIVILEGE.CAMPAIGN.COLLABORATION.INDEX} ifCondition={campaignData?.campaign?.mode !== 'offline'}>
                            <NavItem className='campaign-nav-item'>
                                <NavLink
                                    style={{ cursor: "pointer" }}
                                    className={`campaign-nav-link ${getTabId === 'collaborations' && 'active-campaign-nav'} text-start`}
                                    onClick={() => navigate(`/campaign/${campaignId}/collaborations${getRecentFiltersQuery('collaborations')}`)}
                                >
                                    Collaborations
                                    {

                                        <span className="count-badge mx-2">{
                                            totalCollaborationsCount ? totalCollaborationsCount
                                                : (campaignData?.campaign?.collaborations_count ? campaignData.campaign.collaborations_count
                                                    : 0)
                                        }</span>
                                    }
                                </NavLink>
                            </NavItem>
                        </AccessControl>
                        <AccessControl privilege={PRIVILEGE.CAMPAIGN.PERFORMANCE.INDEX}>
                            <NavItem className='campaign-nav-item'>
                                <NavLink
                                    style={{ cursor: "pointer" }}
                                    className={`campaign-nav-link ${getTabId === 'insights' && 'active-campaign-nav'} text-start`}
                                    onClick={() => navigate(`/campaign/${campaignId}/insights`)}
                                >
                                    Campaign Insights
                                </NavLink>
                            </NavItem>
                        </AccessControl>
                        <AccessControl privilege={PRIVILEGE.CAMPAIGN.APPLICATION.INDEX} ifCondition={campaignData?.campaign?.mode === 'offline'}>
                            <NavItem className='campaign-nav-item'>
                                <NavLink
                                    style={{ cursor: "pointer" }}
                                    className={`campaign-nav-link ${getTabId === 'add-posts' && 'active-campaign-nav'} text-start`}
                                    onClick={() => navigate(`/campaign/${campaignId}/add-posts`)}
                                >
                                    Add Posts
                                </NavLink>
                            </NavItem>
                        </AccessControl>
                        {/* <AccessControl privilege={PRIVILEGE.CAMPAIGN.APPLICATION.INDEX}>
                            <NavItem className='campaign-nav-item'>
                                <NavLink
                                    style={{ cursor: "pointer" }}
                                    className={`campaign-nav-link ${getTabId === 'links-status' && 'active-campaign-nav'} text-start`}
                                    onClick={() => navigate(`/campaign/${campaignId}/links-status`)}
                                >
                                    Links Status
                                </NavLink>
                            </NavItem>
                        </AccessControl> */}
                    </Nav>
                    {filters}
                </div>
                <div className="campaign-page-content my-3">
                    {children}
                </div>
            </div>
            <InfluencerPostModal
                isOpen={!!influencerPostModalVisibile}
                onClose={() => { }}
                postId={influencerPostModalVisibile?.postId!}
                influencer={influencerPostModalVisibile?.influencer!}
                socialAccount={influencerPostModalVisibile?.socialAccount!}
                status={influencerPostModalVisibile?.status}
                preventAction={!!influencerPostModalVisibile?.preventAction}
            />
            {
                configurations?.agency_show_campaign_payments === 'y' && <CampaignPaymentsOffcanvas />
            }
            <ApplicationOffCanvas
                isOpen={!!applicationOffcanvas}
                toggleOffcanvas={() => dispatch(toggleApplicationOffcanvas(null))}
                campaignId={applicationOffcanvas?.campaignId!}
                influencerId={applicationOffcanvas?.influencerId!}
                approve={applicationOffcanvas?.approve}
                reject={applicationOffcanvas?.reject}
                socialAccountId={applicationOffcanvas?.socialAccountId}
            />
            <PostPerformanceOffcanvas
                isOpen={!!postPerformanceOffcanvas}
                collaborationId={postPerformanceOffcanvas?.collaborationId!}
                onClose={() => dispatch(togglePostPerformanceOffcanvas(null))}
            />
            <ApproveApplicationCost 
                application={approveApplicationModal?.application!}
                campaignId={campaignId!}
                isOpen={approveApplicationModal?.isOpen!}
                onClose={() => dispatch(toggleApproveApplicationCostModal(null))}
            />
        </div>
    );
};

export default CampaignLayout;